// src/components/ViewAsDialog.jsx
// Pick a person and see the portal exactly as they see it (their tabs,
// their faxes). Super Admins: anyone, and they can act as them. Admins (with
// "View as" in Roles & Access): Managers and Users in their account, read
// only. Opened from the menu under your name, or "View as" in People.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from './Notifications';
import { fetchUsers } from '../utils/api';
import Loader from './Loader';
import Emo from './Emo';

export default function ViewAsDialog({ onClose }) {
  const { getToken, startViewAs, userInfo } = useAuth();
  const readOnly = userInfo?.roleId !== 'super_admin';
  const { notify } = useNotify();
  const [people, setPeople] = useState(null);
  const [error, setError]   = useState('');
  const [q, setQ]           = useState('');
  const [busy, setBusy]     = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    searchRef.current?.focus();
    fetchUsers(getToken)
      .then(r => setPeople((r.data || []).filter(p => p.canViewAs)))
      .catch(e => setError(e.message));
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [getToken, onClose]);

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (people || []).filter(p => !t || p.email.includes(t) || (p.name || '').toLowerCase().includes(t)
      || (p.roleLabel || '').toLowerCase().includes(t));
  }, [people, q]);

  async function pick(p) {
    setBusy(p.email);
    try {
      await startViewAs(p.email);
      onClose();
    } catch (e) {
      notify({ tone: 'error', title: "Couldn't start View as", detail: e.message });
      setBusy('');
    }
  }

  return (
    <div style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={S.modal} role="dialog" aria-modal="true" aria-labelledby="viewas-title">
        <div style={S.header} className="dlg-head">
          <div>
            <h2 id="viewas-title" style={S.title}><Emo e="👁️" /> View as…</h2>
            <div style={S.sub}>{readOnly
              ? 'See the portal exactly as one of the Managers or Users at your company sees it. It\'s read only: you can look around, but not change or send anything.'
              : 'Use the portal exactly as this person does. Anything you change or send is done as them, and recorded in the Activity log under your name.'}</div>
          </div>
          <button onClick={onClose} style={S.closeBtn} aria-label="Close">✕</button>
        </div>
        <div style={S.body}>
          <input ref={searchRef} value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search name, email or role" aria-label="Search people" style={S.search} />
          {error && <div role="alert" style={S.error}>{error}</div>}
          {!people && !error && <Loader size="sm" />}
          <ul style={S.list}>
            {shown.map(p => (
              <li key={p.email} style={S.row}>
                <div style={{ minWidth: 0 }}>
                  <div style={S.name}>{p.name || p.email}</div>
                  <div style={S.meta}>{p.email} · {p.roleLabel} · {p.groupLabel}</div>
                </div>
                <button onClick={() => pick(p)} disabled={!!busy} style={S.pickBtn}>
                  {busy === p.email ? 'Opening…' : 'View as'}
                </button>
              </li>
            ))}
            {people && !shown.length && <li style={S.muted}>{people.length ? 'Nobody matches your search.' : 'There is nobody you can view as.'}</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgb(var(--c-0f2823-rgb) / 0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: 'var(--c-ffffff)', borderRadius: 14, width: 'min(560px, 100%)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgb(var(--c-000000-rgb) / 0.25)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, padding: '18px 22px', borderBottom: '2px solid var(--c-e0f2f1)', background: 'var(--c-f9fafb)', borderRadius: '14px 14px 0 0' },
  title: { margin: 0, fontSize: 19, fontWeight: 700, color: 'var(--t-00695c)' },
  sub: { fontSize: 13, color: 'var(--t-607d8b)', marginTop: 4, lineHeight: 1.5 },
  closeBtn: { width: 34, height: 34, borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', background: 'var(--c-ffffff)', cursor: 'pointer', fontSize: 15, color: 'var(--t-546e7a)', flexShrink: 0 },
  body: { padding: '14px 22px 20px', overflowY: 'auto' },
  search: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 14, fontFamily: 'inherit', marginBottom: 10 },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '9px 12px', borderRadius: 8, fontSize: 13 },
  muted: { color: 'var(--t-90a4ae)', fontSize: 13, padding: '8px 0' },
  list: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '9px 12px', border: '1px solid var(--c-eceff1)', borderRadius: 10 },
  name: { fontSize: 14, fontWeight: 600, color: 'var(--t-263238)' },
  meta: { fontSize: 12, color: 'var(--t-78909c)', overflowWrap: 'anywhere' },
  pickBtn: { padding: '7px 14px', background: 'var(--c-e65100)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' },
};
